import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Compass, Car, Home as HomeIcon, Backpack, Mountain, Shield, Siren, Truck, Lock, RotateCcw, Headphones } from "lucide-react";
import { api, money, resolveImg, track } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { NewsletterForm } from "../components/Footer";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/a9b6d19e-6de9-407e-aff6-4261a9490cf1/images/28b62a6d5d3ec2cb0d039462035edc867f8a598bb21bba4082f46dbed52cc2a5.jpeg";

const SCENARIOS = [
  { key: "vehicle", icon: Car, label: "Vehicle Emergency", title: "Vehicle Readiness", desc: "Be prepared for breakdowns, roadside emergencies and unexpected situations.", cta: "Build My Vehicle Kit", prompt: "I need a vehicle emergency kit." },
  { key: "home", icon: HomeIcon, label: "Home Preparedness", title: "Home Preparedness", desc: "Build the essentials you need for power outages, severe weather and emergencies.", cta: "Prepare My Home", prompt: "I want to prepare my home for power outages, severe weather and emergencies." },
  { key: "edc", icon: Backpack, label: "Everyday Carry", title: "Everyday Carry", desc: "Practical gear designed to keep you ready wherever the day takes you.", cta: "Build My EDC", prompt: "I want practical everyday-carry equipment." },
  { key: "outdoor", icon: Mountain, label: "Outdoor Adventure", title: "Outdoor Adventure", desc: "Gear for hiking, camping, travel and getting off the beaten path.", cta: "Explore Outdoor Gear", link: "/shop?category=outdoor-survival" },
  { key: "security", icon: Shield, label: "Personal Security", title: "Personal Security", desc: "Practical equipment designed around awareness, readiness and personal protection.", cta: "Explore Security Gear", link: "/shop?category=personal-security" },
  { key: "emergency", icon: Siren, label: "Emergency Preparedness", title: "Emergency Preparedness", desc: "Build a practical supply of essential equipment before you need it.", cta: "Build My Emergency Kit", prompt: "I want a preparedness kit for emergencies and severe weather." },
];

const MISSIONS = [
  { name: "Protect", desc: "Personal security & awareness", link: "/shop?category=personal-security" },
  { name: "Prepare", desc: "Emergency & readiness supplies", link: "/shop?category=preparedness" },
  { name: "Explore", desc: "Outdoor, camping & travel", link: "/shop?category=outdoor-survival" },
  { name: "Carry", desc: "Everyday-carry essentials", link: "/shop?category=everyday-carry" },
  { name: "Respond", desc: "Vehicle & roadside readiness", link: "/shop?good_for=Vehicle" },
  { name: "Secure", desc: "Home & property security", link: "/shop?category=home-security" },
];

const BUDGETS = [
  { cap: "25", title: "Under $25", desc: "Small essentials and everyday gear." },
  { cap: "50", title: "Under $50", desc: "Practical preparedness upgrades." },
  { cap: "100", title: "Under $100", desc: "Build a serious readiness setup." },
];

const TRUST = [
  { icon: Lock, title: "Secure Checkout", text: "Card payments processed by Stripe. USDC crypto accepted." },
  { icon: Truck, title: "Free Shipping $75+", text: "Fast dispatch on qualifying orders from the USA." },
  { icon: RotateCcw, title: "Easy Returns", text: "30-day hassle-free returns on unused gear." },
  { icon: Headphones, title: "Real Support", text: "Questions answered by real people, not bots." },
];

export default function Home({ onOpenAdvisor }) {
  const [featured, setFeatured] = useState([]);
  const [kits, setKits] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/products?best_seller=true&limit=8").then(({ data }) => setFeatured(data.products)).catch(() => {});
    api.get("/kits").then(({ data }) => setKits(data.filter((k) => k.level === "complete"))).catch(() => {});
    api.get("/blog").then(({ data }) => setPosts(data.slice(0, 4))).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 1))).catch(() => {});
  }, []);

  const openScenario = (s) => { track("scenario_select", "homepage", { scenario: s.key }); onOpenAdvisor(s.prompt); };

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-[#121212] text-white overflow-hidden">
        <img src={HERO_IMG} alt="Readiness gear staged on a vehicle tailgate before a mountain storm" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 pb-14 md:pt-32 md:pb-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#8FA84E] mb-4 font-mono">Shodan Security — Protect. Prepare. Perform.</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-[0.95] max-w-3xl">
            Be Ready for<br />What's Next.
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-white/80">From everyday carry to vehicle emergencies, outdoor adventures, severe weather and home preparedness, Shodan Security helps you choose the gear you actually need to protect, prepare and perform.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button data-testid="hero-build-kit-btn" onClick={() => { track("hero_cta", "homepage", { cta: "build_my_readiness_kit" }); onOpenAdvisor(); }}
              className="bg-[#4A5D23] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">
              Build My Readiness Kit
            </button>
            <Link data-testid="hero-explore-gear-btn" to="/shop" onClick={() => track("hero_cta", "homepage", { cta: "explore_gear" })}
              className="border border-white text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors duration-150">
              Explore Gear
            </Link>
          </div>
          <div className="mt-10">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 font-mono mb-3">What are you preparing for?</p>
            <div className="flex flex-wrap gap-2">
              {SCENARIOS.map((s) => s.prompt ? (
                <button key={s.key} data-testid={`hero-scenario-${s.key}`} onClick={() => openScenario(s)}
                  className="flex items-center gap-2 border border-white/25 bg-black/30 backdrop-blur-sm px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] hover:border-[#4A5D23] transition-colors duration-150">
                  <s.icon size={14} className="text-[#8FA84E]" /> {s.label}
                </button>
              ) : (
                <Link key={s.key} data-testid={`hero-scenario-${s.key}`} to={s.link} onClick={() => track("scenario_select", "homepage", { scenario: s.key })}
                  className="flex items-center gap-2 border border-white/25 bg-black/30 backdrop-blur-sm px-4 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] hover:border-[#4A5D23] transition-colors duration-150">
                  <s.icon size={14} className="text-[#8FA84E]" /> {s.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT ARE YOU PREPARING FOR? */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">What Are You Preparing For?</h2>
        <p className="text-black/50 text-sm mt-2">Start with your mission. We'll help you find the gear.</p>
        <div data-testid="scenario-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10 mt-10">
          {SCENARIOS.map((s) => (
            <div key={s.key} data-testid={`scenario-card-${s.key}`} className="group bg-white p-7 flex flex-col hover:bg-[#F4F3F0] transition-colors duration-150">
              <div className="w-11 h-11 flex items-center justify-center bg-[#4A5D23]/10 text-[#4A5D23]"><s.icon size={22} /></div>
              <h3 className="font-heading font-bold uppercase tracking-tight text-lg mt-4">{s.title}</h3>
              <p className="text-sm text-black/55 mt-2 flex-1">{s.desc}</p>
              {s.prompt ? (
                <button data-testid={`scenario-cta-${s.key}`} onClick={() => openScenario(s)}
                  className="mt-5 inline-flex items-center gap-2 self-start text-xs font-bold uppercase tracking-widest text-[#4A5D23] hover:gap-3 transition-all duration-150">
                  {s.cta} <ArrowRight size={14} />
                </button>
              ) : (
                <Link data-testid={`scenario-cta-${s.key}`} to={s.link} onClick={() => track("scenario_select", "homepage", { scenario: s.key })}
                  className="mt-5 inline-flex items-center gap-2 self-start text-xs font-bold uppercase tracking-widest text-[#4A5D23] hover:gap-3 transition-all duration-150">
                  {s.cta} <ArrowRight size={14} />
                </Link>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* READY KITS */}
      <section className="bg-[#F4F3F0] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Curated Solutions</p>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold mt-1">Ready Kits</h2>
              <p className="text-black/50 text-sm mt-1">Skip the guesswork. Start with a complete solution.</p>
            </div>
            <Link data-testid="view-all-kits" to="/kits" onClick={() => track("kit_click", "homepage", { kit: "view_all" })} className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-[#4A5D23] transition-colors duration-150">All Kits <ArrowRight size={14} /></Link>
          </div>
          <div data-testid="ready-kits-grid" className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {kits.map((kit) => (
              <div key={kit.slug} data-testid={`kit-card-${kit.slug}`} className="border border-black/10 bg-white flex flex-col">
                <Link to={`/kit/${kit.slug}`} onClick={() => track("kit_click", "homepage", { kit: kit.slug })} className="relative block aspect-[16/7] overflow-hidden bg-[#EAE8E3]">
                  <img src={resolveImg(kit.image)} alt={kit.name} loading="lazy" className="w-full h-full object-cover" />
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest bg-[#4A5D23] text-white px-2 py-1">{(kit.products || []).length}-Piece Ready Kit</span>
                </Link>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold uppercase tracking-tight text-lg">{kit.name}</h3>
                  <p className="text-xs text-black/50 mt-1">{kit.description}</p>
                  <div className="mt-3 border border-black/10 divide-y divide-black/5">
                    {(kit.products || []).map((p) => (
                      <div key={p.slug} className="flex items-center gap-2.5 px-3 py-1.5 text-xs">
                        <img src={resolveImg(p.images?.[0])} alt={p.name} loading="lazy" className="w-7 h-7 object-cover bg-[#F4F3F0] border border-black/5 shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-black/70">{p.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-baseline gap-3 flex-wrap">
                    <span className="font-heading font-black text-2xl">{money(kit.bundle_price)}</span>
                    <span className="text-xs text-black/40 line-through">{money(kit.retail_value)} value</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-[#4A5D23] text-white px-2 py-1">Save {money(kit.savings)}</span>
                  </div>
                  <Link data-testid={`view-kit-home-${kit.slug}`} to={`/kit/${kit.slug}`} onClick={() => track("kit_click", "homepage", { kit: kit.slug })}
                    className="mt-4 block w-full text-center bg-[#121212] text-white py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">
                    View This Kit
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEAR ADVISOR */}
      <section id="advisor" className="bg-[#121212] text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Compass size={26} className="text-[#8FA84E]" />
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#8FA84E] font-mono">The Shodan Gear Advisor</p>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">Not Sure What You Need?</h2>
          <p className="font-heading text-lg md:text-xl uppercase font-bold tracking-tight text-white/80 mt-1">Let Shodan build your gear list.</p>
          <p className="mt-4 max-w-xl text-white/70 text-sm md:text-base">Tell us where you're going, what you're preparing for, and what matters most to you. Our Gear Advisor will help you identify the equipment that fits your situation.</p>
          <button data-testid="advisor-section-btn" onClick={() => { track("advisor_cta", "homepage", { cta: "ask_the_gear_advisor" }); onOpenAdvisor(); }}
            className="mt-7 inline-flex items-center gap-2 bg-[#4A5D23] px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">
            <Compass size={16} /> Ask the Gear Advisor
          </button>
        </div>
      </section>

      {/* SHOP BY MISSION */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">Shop by Mission</h2>
        <p className="text-black/50 text-sm mt-2">No jargon. Just what you're trying to accomplish.</p>
        <div data-testid="mission-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-black/10 border border-black/10 mt-10">
          {MISSIONS.map((m) => (
            <Link key={m.name} data-testid={`mission-${m.name.toLowerCase()}`} to={m.link} onClick={() => track("mission_click", "homepage", { mission: m.name })}
              className="group bg-white p-6 hover:bg-[#121212] transition-colors duration-200">
              <h3 className="font-heading font-black uppercase tracking-tight text-lg group-hover:text-white transition-colors duration-200">{m.name}</h3>
              <p className="text-[11px] text-black/50 mt-1 group-hover:text-white/60 transition-colors duration-200">{m.desc}</p>
              <ArrowRight size={15} className="mt-4 text-[#4A5D23] group-hover:text-[#8FA84E] transition-colors duration-200" />
            </Link>
          ))}
        </div>
      </section>

      {/* SHODAN ESSENTIALS */}
      <section className="bg-[#F4F3F0] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">Shodan Essentials</h2>
              <p className="text-black/50 text-sm mt-1">Gear people are choosing to stay ready.</p>
            </div>
            <Link data-testid="view-all-products" to="/shop?filter=best" className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-[#4A5D23] transition-colors duration-150">View All <ArrowRight size={14} /></Link>
          </div>
          <div data-testid="featured-products-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* READY FOR ANY BUDGET */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">Ready for Any Budget</h2>
        <p className="text-black/50 text-sm mt-2">Preparedness doesn't have to break the bank.</p>
        <div data-testid="budget-grid" className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-black/10 border border-black/10 mt-10">
          {BUDGETS.map((b) => (
            <Link key={b.cap} data-testid={`budget-under-${b.cap}`} to={`/shop?max_price=${b.cap}`} onClick={() => track("budget_click", "homepage", { cap: b.cap })}
              className="group bg-white p-8 hover:bg-[#4A5D23] transition-colors duration-200">
              <span className="font-heading font-black text-3xl tracking-tighter uppercase group-hover:text-white transition-colors duration-200">{b.title}</span>
              <p className="text-sm text-black/55 mt-2 group-hover:text-white/70 transition-colors duration-200">{b.desc}</p>
              <span className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#4A5D23] group-hover:text-white transition-colors duration-200">Shop Now <ArrowRight size={14} /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* FIELD GUIDE */}
      <section className="bg-[#F4F3F0] py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-bold">The Shodan Field Guide</h2>
              <p className="text-black/50 text-sm mt-1">Practical knowledge for staying ready.</p>
            </div>
            <Link to="/field-guide" onClick={() => track("field_guide_click", "homepage", { article: "explore_all" })} className="hidden sm:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest hover:text-[#4A5D23] transition-colors duration-150">Explore the Field Guide <ArrowRight size={14} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {posts.map((post) => (
              <Link key={post.slug} data-testid={`blog-card-${post.slug}`} to={`/field-guide/${post.slug}`} onClick={() => track("field_guide_click", "homepage", { article: post.slug })}
                className="group bg-white border border-black/10 transition-transform duration-150 hover:-translate-y-0.5">
                <div className="aspect-[16/9] overflow-hidden bg-[#EAE8E3]">
                  <img src={post.image} alt={post.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#4A5D23]">{post.category}</span>
                  <h3 className="font-heading font-bold tracking-tight mt-2 leading-snug group-hover:text-[#4A5D23] transition-colors duration-150">{post.title}</h3>
                  <p className="text-xs text-black/50 mt-2 line-clamp-2">{post.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/field-guide" className="sm:hidden mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#4A5D23]">Explore the Field Guide <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* NEXT EVENT */}
      {events.length > 0 && (
        <section data-testid="next-event-section" className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="border border-black/10 bg-white grid grid-cols-1 md:grid-cols-2">
            <div className="p-8 md:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono mb-3">Next Event</p>
              <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">{events[0].name}</h2>
              <div className="mt-4 space-y-2 text-sm text-black/70">
                <p className="flex items-center gap-2"><Calendar size={15} className="text-[#4A5D23]" /> {events[0].date}{events[0].end_date !== events[0].date ? ` – ${events[0].end_date}` : ""}</p>
                <p className="flex items-center gap-2"><MapPin size={15} className="text-[#4A5D23]" /> {events[0].location} · {events[0].booth}</p>
              </div>
              <p className="text-sm text-black/60 mt-4">{events[0].description}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link data-testid="meet-us-btn" to="/events" className="bg-[#121212] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Meet Us In Person</Link>
                <Link to="/shop" className="border border-black px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors duration-150">Can't Make It? Shop Online</Link>
              </div>
            </div>
            <div className="hidden md:block bg-[#121212] relative overflow-hidden">
              <img src="https://images.unsplash.com/photo-1559521783-1d1599583485?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Shodan Security event booth gear" loading="lazy" className="w-full h-full object-cover opacity-70" />
            </div>
          </div>
        </section>
      )}

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
        <div data-testid="trust-grid" className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {TRUST.map((t) => (
            <div key={t.title} className="bg-white p-6">
              <t.icon size={20} className="text-[#4A5D23]" />
              <h3 className="font-heading font-bold uppercase tracking-tight text-sm mt-3">{t.title}</h3>
              <p className="text-xs text-black/50 mt-1">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* EMAIL CAPTURE */}
      <section className="bg-[#121212] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <h2 className="font-heading text-2xl md:text-3xl tracking-tight uppercase font-bold">Get Ready Before You Need To.</h2>
            <p className="text-white/60 text-sm mt-2">Get the Shodan Preparedness Checklist plus practical readiness tips, new gear alerts and exclusive offers.</p>
          </div>
          <NewsletterForm source="homepage" dark />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#4A5D23] text-white py-16 md:py-20 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight uppercase font-black">Don't Wait Until You Need It.</h2>
          <p className="text-white/75 text-sm md:text-base mt-3">Get prepared with the right gear for your situation.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button data-testid="final-build-kit-btn" onClick={() => { track("hero_cta", "homepage", { cta: "final_build_my_readiness_kit" }); onOpenAdvisor(); }}
              className="bg-[#121212] text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-black transition-colors duration-150">
              Build My Readiness Kit
            </button>
            <Link data-testid="final-shop-all-btn" to="/shop" className="border border-white text-white px-8 py-4 text-sm font-bold uppercase tracking-widest hover:bg-white hover:text-[#4A5D23] transition-colors duration-150">
              Shop All Gear
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
